import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { HubunganKeluarga } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: HubunganKeluarga) => {
  const response = await api.post("/master/hubungan-keluarga/store", newData)
  return response.data
}

const patchData = async (newData: HubunganKeluarga) => {
  const response = await api.patch("/master/hubungan-keluarga/update", newData)
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_hubungan_keluarga: number
}) => {
  const response = await api({
    url: "/master/hubungan-keluarga/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: HubunganKeluarga | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<HubunganKeluarga>({
    id_ref_hubungan_keluarga: selectedRow?.id_ref_hubungan_keluarga || 0,
    hubungan_keluarga: selectedRow?.hubungan_keluarga || "",
    is_status_tampil: selectedRow?.is_status_tampil ?? true,
    is_wajib: selectedRow?.is_wajib ?? false,
    is_only_one: selectedRow?.is_only_one ?? false,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listHubunganKeluarga"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jenis Berkas"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.hubungan_keluarga}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jenis Berkas" : "Update Data Jenis Berkas"}
      />
      <div>
        <Label>Hubungan Keluarga</Label>
        <Input
          placeholder="Ketik hubungan keluarga..."
          value={form.hubungan_keluarga}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              hubungan_keluarga: event.target.value,
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="is_only_one"
          checked={form.is_only_one}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_only_one: !prev.is_only_one,
            }))
          }
        />
        <Label htmlFor="is_only_one">Maksimal 1 inputan (tidak/ya)</Label>
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={Boolean(form.is_status_tampil)}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_status_tampil: !prev.is_status_tampil,
            }))
          }
        />
        <Label htmlFor="status">Status (Nonaktif/Aktif)</Label>
      </div>

      <div className="flex gap-3">
        <Switch
          id="is_wajib"
          checked={Boolean(form.is_wajib)}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_wajib: !prev.is_wajib,
            }))
          }
        />
        <Label htmlFor="is_wajib">Wajib? (tidak/ya)</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
